import { useCallback, useState } from 'react';
import type { BannerState, View } from '../types';

interface DrawerState {
  isOpen: boolean;
  view: View;
  banner: BannerState | null;
}

const INITIAL_STATE: DrawerState = {
  isOpen: false,
  view: 'picker',
  banner: null,
};

export function useNewOrderDrawerState() {
  const [state, setState] = useState<DrawerState>(INITIAL_STATE);

  const openDrawer = useCallback(() => {
    setState({ isOpen: true, view: 'picker', banner: null });
  }, []);

  const closeDrawer = useCallback(() => {
    setState(INITIAL_STATE);
  }, []);

  const goToAddNew = useCallback(() => {
    setState((s) => ({ ...s, view: 'addNew' }));
  }, []);

  const backToPicker = useCallback(() => {
    setState((s) => ({ ...s, view: 'picker' }));
  }, []);

  const showSuccessBanner = useCallback((title: string, detail: string) => {
    setState((s) => ({
      ...s,
      view: 'picker',
      banner: { kind: 'success', title, detail },
    }));
  }, []);

  const showErrorBanner = useCallback(
    (
      title: string,
      detail: string,
      retryPayload?: BannerState['retryPayload']
    ) => {
      // Stay in the current view — the banner is rendered above both the
      // picker rows and the add-new form, so the user sees the error
      // wherever they were when it fired. Critically, this keeps the
      // add-new form's contents intact on a create-failure so the user
      // can edit and re-submit rather than losing what they typed.
      setState((s) => ({
        ...s,
        banner: { kind: 'error', title, detail, retryPayload },
      }));
    },
    []
  );

  const dismissBanner = useCallback(() => {
    setState((s) => ({ ...s, banner: null }));
  }, []);

  return {
    isOpen: state.isOpen,
    view: state.view,
    banner: state.banner,
    openDrawer,
    closeDrawer,
    goToAddNew,
    backToPicker,
    showSuccessBanner,
    showErrorBanner,
    dismissBanner,
  };
}
